import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { FairCommitment, FaqItem } from "../components/ui.jsx";
import { useLocaleNav, useCurrentLocale } from "../lib/useLocaleNav.js";
import { PLAN_IDS, FEATURED_PLAN, FEATURE_ICONS, CARDS_ICON, pricesForLocale, formatAmount } from "../config/plans.js";

// Sign-up happens on the app domain, where the plan and billing period are
// picked — so every trial CTA on this page opens the same entry point.
const SIGNUP_URL = "https://my.tabletalk.app/";

// The Enterprise tier is quoted per account, so its CTA books a call instead.
const DEMO_URL = "https://calendly.com/matei-delipal/tabletalk";

export default function Pricing() {
  const go = useLocaleNav();
  const locale = useCurrentLocale();
  const [billing, setBilling] = useState("monthly");
  const openSignup = () => window.open(SIGNUP_URL, "_blank", "noopener,noreferrer");

  return (
    <main>
      <Hero />
      <Plans locale={locale} billing={billing} onToggleBilling={setBilling} onClaim={openSignup} go={go} />
      <FairCommitment />
      <FAQ />
      <FinalCTA onClaim={openSignup} />
    </main>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--bg)" }}>
      <div className="container" style={{ padding: "56px 24px 96px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
          <span style={{ width: 22, height: 1, background: "var(--persimmon)" }} />
          {t("pricing.eyebrow")}
        </div>
        <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>
          {t("pricing.heroHeadline")}<br />
          <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("pricing.heroHeadlineItalic")}</span>
        </h1>
        <p style={{ marginTop: 32, maxWidth: 720, fontSize: "clamp(18px, 1.4vw, 21px)", lineHeight: 1.6, color: "var(--ink-2)" }}>
          {t("pricing.heroIntro")}
        </p>
      </div>
    </section>
  );
}

function BillingToggle({ billing, onToggle }) {
  const { t } = useTranslation();
  const pill = (key, label, sub) => {
    const active = billing === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onToggle(key)}
        aria-pressed={active}
        style={{
          all: "unset",
          cursor: "pointer",
          padding: "10px 20px",
          borderRadius: 999,
          background: active ? "var(--bg)" : "transparent",
          color: active ? "var(--ink)" : "rgba(255,255,255,.82)",
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: 14,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          transition: "background .2s ease, color .2s ease"
        }}
      >
        {label}
        {sub && (
          <span style={{
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: active ? "var(--persimmon)" : "var(--marigold)"
          }}>
            {sub}
          </span>
        )}
      </button>
    );
  };
  return (
    <div role="group" aria-label={t("pricing.billingAria")}
      style={{ display: "inline-flex", padding: 4, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.20)", borderRadius: 999 }}>
      {pill("monthly", t("pricing.billingMonthly"))}
      {pill("yearly", t("pricing.billingYearly"), t("pricing.billingSaveBadge"))}
    </div>
  );
}

function Plans({ locale, billing, onToggleBilling, onClaim, go }) {
  const { t } = useTranslation();
  const cfg = pricesForLocale(locale);
  return (
    <section style={{ background: "var(--ink)", padding: "100px 0 120px" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
          <BillingToggle billing={billing} onToggle={onToggleBilling} />
        </div>

        <div className="plan-grid">
          {PLAN_IDS.map(id => (
            <PlanCard
              key={id}
              id={id}
              cfg={cfg}
              locale={locale}
              billing={billing}
              onClaim={onClaim}
            />
          ))}
        </div>

        <Enterprise />

        <div style={{ marginTop: 24, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,.62)", fontFamily: "Inter, sans-serif" }}>
          {t("pricing.card.agreeText")}{" "}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); go("terms-of-sale"); }}
            style={{ color: "var(--persimmon)", textDecoration: "none", borderBottom: "1px solid var(--persimmon)" }}
          >
            {t("pricing.card.termsOfSaleLink")}
          </a>.
        </div>
      </div>
      <style>{`
        .plan-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          column-gap: 24px;
          /* No row gap: side by side the parent's rows are the cards' internal
             bands, and a gutter there would push every band apart. The stacked
             layout below re-adds it between cards. */
          row-gap: 0;
          align-items: stretch;
        }
        /* Each card owns five rows of the parent grid — heading, offer line,
           price, CTA, features — and shares their heights with its siblings, so
           the bands line up across the row whatever the translation length. */
        .plan-card {
          grid-row: span 5;
          display: grid;
          grid-template-rows: subgrid;
          row-gap: 0;
          padding: 26px;
          border-radius: 12px;
        }
        .plan-enterprise {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          column-gap: 48px;
          row-gap: 0;
          align-items: start;
        }
        .plan-enterprise .ent-label { grid-column: 1; grid-row: 1; margin-bottom: 10px; }
        .plan-enterprise .ent-main  { grid-column: 1; grid-row: 2; }
        /* Row 2 as well, so its first line starts level with the heading.
           The nudge optically centres 15px copy against the much taller
           heading line box. */
        .plan-enterprise .ent-copy  { grid-column: 2; grid-row: 2; padding-top: 5px; }
        @media (max-width: 1080px) {
          .plan-grid { grid-template-columns: 1fr; row-gap: 24px; max-width: 560px; margin: 0 auto; }
          .plan-enterprise { grid-template-columns: 1fr; max-width: 560px; margin-left: auto; margin-right: auto; }
          /* Stacked: back to source order, with the copy spaced off the CTA.
             Spacing goes on padding, not margin — the <p> sets margin inline,
             which would win over this rule. */
          .plan-enterprise .ent-label,
          .plan-enterprise .ent-main,
          .plan-enterprise .ent-copy { grid-column: 1; grid-row: auto; }
          .plan-enterprise .ent-copy { padding-top: 28px; }
        }
      `}</style>
    </section>
  );
}

function PlanCard({ id, cfg, locale, billing, onClaim }) {
  const { t } = useTranslation();
  const plan = cfg[id];
  const featured = id === FEATURED_PLAN;
  const yearly = billing === "yearly";

  const amount = yearly ? plan.yearly : plan.monthly;
  const features = t(`pricing.plans.${id}.features`, { returnObjects: true });
  const icons = FEATURE_ICONS[id] || [];

  // "Everything in Starter" — the previous tier's name, bolded inside the
  // sentence the way the translation places it.
  const prevId = PLAN_IDS[PLAN_IDS.indexOf(id) - 1];
  const inherits = t(`pricing.plans.${id}.inherits`, { defaultValue: "" });

  // Struck-through standard price, matched to the billing period on show.
  const wasAmount = yearly ? plan.wasYearly : plan.wasMonthly;
  const wasPrice = wasAmount ? formatAmount(wasAmount, cfg, locale) : null;

  // The card is a subgrid of `.plan-grid`, so the heading / offer / price / CTA
  // / features bands each get one shared row across all three columns. That
  // keeps them on one line whatever the translation length — German
  // descriptions run to four lines where English runs to two. Browsers without
  // subgrid fall back to independently sized rows, which still reads fine, just
  // without the cross-card alignment.
  return (
    <article className="plan-card" style={{
      background: "var(--bg)",
      // Same border width on every card so the three stay pixel-aligned.
      border: featured ? "2px solid var(--persimmon)" : "2px solid var(--line)",
      boxShadow: featured
        ? "0 30px 80px -20px rgba(232, 90, 26, 0.42), 0 14px 40px -16px rgba(0, 0, 0, 0.5)"
        : "0 14px 40px -18px rgba(0, 0, 0, 0.55)"
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <span style={{
            fontFamily: "Satoshi, Inter, sans-serif",
            fontWeight: 700,
            fontSize: "clamp(21px, 1.7vw, 25px)",
            color: "var(--persimmon)",
            letterSpacing: "-0.02em"
          }}>
            {t(`pricing.plans.${id}.name`)}
          </span>
          {featured && (
            <Badge bg="var(--persimmon)" color="#fff">{t("pricing.plans.mostPopular")}</Badge>
          )}
        </div>

        <p style={{
          margin: "10px 0 0",
          fontFamily: "Inter, sans-serif",
          fontSize: 13.5,
          lineHeight: 1.55,
          color: "var(--muted)"
        }}>
          {t(`pricing.plans.${id}.tagline`)}
        </p>
      </div>

      {/* Its own band, kept in the flow even when empty, so the prices below sit
          on one baseline across the row. */}
      <div style={{ marginTop: 22, paddingTop: 22, borderTop: "1px solid var(--line)" }}>
        {wasPrice && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--muted)" }}>
              <s style={{ textDecorationThickness: 1 }}>{wasPrice}</s>
              {" "}
              {yearly ? t("pricing.plans.perYear") : t("pricing.plans.perMonth")}
            </span>
            <Badge bg="var(--bg-2)" color="var(--ink)">{t("pricing.plans.limitedOffer")}</Badge>
          </div>
        )}
      </div>

      <div style={{ paddingTop: 8 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
          <span className="tt-big" style={{ fontSize: "clamp(38px, 3.6vw, 50px)", color: "var(--ink)", lineHeight: 0.95 }}>
            {formatAmount(amount, cfg, locale)}
          </span>
          <span className="tt-big" style={{ fontSize: "clamp(28px, 2.6vw, 36px)", color: "var(--ink)", lineHeight: 0.95 }}>
            {yearly ? t("pricing.plans.perYear") : t("pricing.plans.perMonth")}
          </span>
        </div>
        <div style={{ marginTop: 14, fontSize: 13, color: "var(--muted)", fontFamily: "Inter, sans-serif" }}>
          {t("pricing.plans.perLocation")}
        </div>
      </div>

      <div style={{ paddingTop: 22 }}>
        <button
          type="button"
          onClick={onClaim}
          className="btn"
          style={{
            width: "100%",
            background: featured ? "var(--persimmon)" : "transparent",
            color: featured ? "#fff" : "var(--ink)",
            borderColor: featured ? "var(--persimmon)" : "var(--line-2)"
          }}
        >
          {t("common.claimFreeWeek")} <Icon name="arrow-right" size={16} />
        </button>
      </div>

      <div style={{ marginTop: 26, paddingTop: 26, borderTop: "1px solid var(--line)" }}>
        <SectionLabel>{t("pricing.plans.whatYouGet")}</SectionLabel>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {inherits && (
            <li style={{ display: "flex", gap: 10, alignItems: "center", padding: "4px 0 8px", fontSize: 13.5, color: "var(--ink)" }}>
              <Icon name="check" size={16} style={{ flexShrink: 0, color: "var(--persimmon)" }} />
              <Marked label={inherits} phrase={prevId ? t(`pricing.plans.${prevId}.name`) : ""} />
            </li>
          )}
          {features.map((f, i) => (
            <FeatureRow key={f} icon={icons[i]}>{f}</FeatureRow>
          ))}
          <FeatureRow icon={CARDS_ICON} bold>{t(`pricing.plans.${id}.cards`)}</FeatureRow>
        </ul>
      </div>
    </article>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      paddingBottom: 10,
      fontFamily: "Satoshi, Inter, sans-serif",
      fontWeight: 700,
      fontSize: 13.5,
      letterSpacing: "-0.01em",
      color: "var(--ink)"
    }}>
      {children}
    </div>
  );
}

// The fourth tier sits as a full-width band under the three cards rather than a
// fourth column: it has no fixed price, so it neither needs the billing toggle
// nor benefits from being compared row-by-row against the others.
function Enterprise() {
  const { t } = useTranslation();
  return (
    <article
      className="plan-enterprise"
      style={{
        marginTop: 24,
        background: "var(--bg)",
        border: "2px solid var(--line)",
        borderRadius: 12,
        padding: 32,
        boxShadow: "0 14px 40px -18px rgba(0, 0, 0, 0.55)"
      }}
    >
      {/* The plan name gets a grid row to itself so the copy on the right can
          start level with the heading rather than with the name above it. */}
      <div className="ent-label" style={{
        fontFamily: "Satoshi, Inter, sans-serif",
        fontWeight: 700,
        fontSize: "clamp(21px, 1.7vw, 25px)",
        color: "var(--persimmon)",
        letterSpacing: "-0.02em"
      }}>
        {t("pricing.enterprise.name")}
      </div>

      <div className="ent-main">
        {/* A call to action rather than a figure, so it takes the heading
            treatment instead of the cards' oversized price style. */}
        <div style={{
          fontFamily: "Satoshi, Inter, sans-serif",
          fontWeight: 700,
          fontSize: "clamp(27px, 2.5vw, 35px)",
          lineHeight: 1.12,
          letterSpacing: "-0.03em",
          color: "var(--ink)"
        }}>
          {t("pricing.enterprise.heading")}
        </div>

        <button
          type="button"
          onClick={() => window.open(DEMO_URL, "_blank", "noopener,noreferrer")}
          className="btn"
          style={{ marginTop: 24, background: "var(--persimmon)", color: "#fff", borderColor: "var(--persimmon)" }}
        >
          {t("pricing.enterprise.cta")} <Icon name="arrow-right" size={16} />
        </button>
      </div>

      <p className="ent-copy" style={{
        margin: 0,
        fontFamily: "Inter, sans-serif",
        fontSize: 15,
        lineHeight: 1.6,
        color: "var(--ink-2)"
      }}>
        {t("pricing.enterprise.tagline")}
      </p>
    </article>
  );
}

function Badge({ bg, color, children }) {
  return (
    <span style={{
      flexShrink: 0,
      background: bg,
      color,
      borderRadius: 4,
      padding: "5px 9px",
      fontFamily: "Inter, sans-serif",
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: ".14em",
      textTransform: "uppercase",
      whiteSpace: "nowrap"
    }}>
      {children}
    </span>
  );
}

function FeatureRow({ icon, bold = false, children }) {
  return (
    <li style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      background: "var(--bg-2)",
      borderRadius: 8,
      padding: "10px 12px",
      fontFamily: "Inter, sans-serif",
      fontSize: 13.5,
      lineHeight: 1.4,
      fontWeight: bold ? 600 : 400,
      color: "var(--ink)"
    }}>
      <Icon name={icon} size={16} style={{ flexShrink: 0, color: "var(--ink-2)" }} />
      <span>{children}</span>
    </li>
  );
}

// Bolds one phrase inside a translated sentence — the plan name in "Everything
// in **Starter**". Each language puts it in a different position, so it's found
// rather than templated.
function Marked({ label, phrase }) {
  const at = phrase ? label.indexOf(phrase) : -1;
  if (at === -1) return <span>{label}</span>;
  return (
    <span>
      {label.slice(0, at)}
      <strong style={{ fontWeight: 600 }}>{phrase}</strong>
      {label.slice(at + phrase.length)}
    </span>
  );
}

function FAQ() {
  const { t } = useTranslation();
  const faqs = t("pricing.faqs", { returnObjects: true });
  return (
    <section style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
          <span style={{ width: 22, height: 1, background: "var(--persimmon)" }} />
          {t("pricing.faqEyebrow")}
        </div>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 24, marginBottom: 48 }}>
          {t("pricing.faqHeadline")} <span style={{ color: "var(--persimmon)" }}>{t("pricing.faqHeadlineSpan")}</span>
        </h2>
        {faqs.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} />
        ))}
      </div>
    </section>
  );
}

function FinalCTA({ onClaim }) {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--ink)", color: "var(--bg)", padding: "180px 0" }}>
      <div className="container">
        <h2 className="tt-h tt-h-xl" style={{ margin: 0, maxWidth: 1100, color: "var(--bg)" }}>
          {t("pricing.finalCta.headline")}{" "}
          <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, color: "var(--persimmon)" }}>{t("pricing.finalCta.headlineItalic")}</span>
        </h2>
        <p style={{ margin: "40px 0 0", fontFamily: "Inter, sans-serif", fontSize: "clamp(18px, 1.4vw, 22px)", lineHeight: 1.55, color: "rgba(255,255,255,.78)", maxWidth: 720 }}>
          {t("common.freeTrialNote")}
        </p>
        <div style={{ marginTop: 48 }}>
          <button type="button" onClick={onClaim} className="btn btn-lg" style={{ background: "var(--persimmon)", color: "var(--ink)" }}>
            {t("common.claimFreeWeek")} <Icon name="arrow-right" size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
